/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionExportData';

const ActionExportDataManager: ActionManagerFn<'ActionExportData'> = (
  platform: Platform,
): ActionManager<'ActionExportData'> => {
  return {
    ...baseActionManager(platform, 'ActionExportData'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Target Grid Item ID',
        type: 'ItemID',
        required: true,
        initialFilter: 'DataGrid',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (
            item?.type !== 'DataGrid' &&
            item?.type !== 'CanvasGrid' &&
            item?.type !== 'PivotTable'
          ) {
            return `Item id '${value}' doesn't belong to a Grid`;
          }
          return '';
        },
      },
      async: {
        label: 'Async',
        type: 'boolean',
      },
      downloadAsZip: {
        label: 'Download as zip',
        type: 'boolean',
      },
      exportFooter: {
        label: 'Export Footer',
        type: 'boolean',
      },
      showReportInformation: {
        label: 'Show Report Information',
        type: 'boolean',
      },
      format: {
        label: 'Format Type',
        options: toOptions(['CSV', 'XLSX']),
        type: 'select',
      },
      fileNameExpression: {
        label: 'File Name',
        type: 'expression',
        returnType: async () => 'string',
      },
      enableEmailAlerts: {
        label: 'Enable Email Alerts',
        type: 'boolean',
      },
      emailSubjectExpression: {
        label: 'Email Subject',
        type: 'expression',
        returnType: async () => 'string',
      },
      emailBodyExpression: {
        label: 'Email Body',
        type: 'expression',
        helpText:
          'Use $.export.link to access the download link from async export.',
        returnType: async () => 'string',
      },
      headerNameExpression: {
        label: 'Header Name',
        type: 'expression',
        returnType: async () => 'string',
      },
      templateCodeExpression: {
        label: 'Custom Template Code',
        type: 'expression',
        returnType: async () => 'string',
      },
    },
    name: 'Export Data',
    getDisplayText: (item) =>
      `${item.props.async ? 'Async ' : ''} ${item.props.format} - ${
        item.props.targetItemId
      }`,
    description: `**Overview**<br/>
The Export Data action is a comprehensive reporting component designed to extract data from grid-based UI components (DataGrid, CanvasGrid, or PivotTable) and generate downloadable files. It supports both immediate client-side downloads and asynchronous server-side processing for handling larger datasets.<br/><br/>
This component offers extensive configuration options for the output file, including format selection (CSV or XLSX), file naming conventions, and the inclusion of metadata like report headers and footers. It also supports large-scale exports through an Async mode, which can be configured to notify users via email upon completion, including a direct download link in the message body.

**Key Features**<br/>
* **Multi-Component Support:** Validates and accepts three distinct target component types: DataGrid, CanvasGrid, and PivotTable.
* **Asynchronous Processing:** Provides an async mode for background processing, unlocking additional capabilities like email notifications (enableEmailAlerts) to avoid browser locking during large exports.
* **Format Flexibility:** Supports exporting to CSV and XLSX formats.
* **Conditional Configuration:** Uses visibility logic (isPropertyVisible) to restrict Report Information/Header settings to XLSX, and restrict Email settings to async-enabled flows.
* **Customization:** Supports custom template codes, dynamic file naming, and configurable headers/footers.

**Properties**<br/>
* **Target Grid Item ID (Required):** An ItemID property identifying the component to export.
  * *Validation:* Must be a DataGrid, CanvasGrid, or PivotTable.
* **Async:** A boolean toggle to enable background processing.
* **Download as zip:** A boolean toggle to compress the output file.
* **Export Footer:** A boolean toggle to include footer data in the export.
* **Show Report Information:** A boolean toggle to include metadata in the report.
  * *Visibility:* Only visible when Format is XLSX.
* **Format Type:** A select property to choose the output file format (CSV or XLSX).
* **File Name:** An expression property defining the name of the exported file.
* **Enable Email Alerts:** A boolean toggle to send an email when the export finishes.
  * *Visibility:* Only visible if async is true.
* **Email Subject:** An expression defining the notification email subject line.
  * *Visibility:* Only visible if enableEmailAlerts is true.
* **Email Body:** An expression defining the notification email content.
  * *Feature:* Can access the download link via $.export.link.
  * *Visibility:* Only visible if enableEmailAlerts is true.
* **Header Name:** An expression defining the custom header text.
  * *Visibility:* Only visible if showReportInformation is true and Format is XLSX.
* **Custom Template Code:** An expression property for advanced template definition.

**Use Cases**<br/>
* **Standard Reporting:** A "Download Excel" button on a dashboard that downloads the visible grid data.
* **Large Dataset Export:** Running a background export for very large datasets and emailing a download link when processing completes, so the user can continue working.`,
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'enableEmailAlerts':
          return props.async;
        case 'emailSubjectExpression':
        case 'emailBodyExpression':
          return props.enableEmailAlerts && props.async;
        case 'headerNameExpression':
          return props.showReportInformation && props.format === 'XLSX';
        case 'showReportInformation':
          return props.format === 'XLSX';
        default:
          return true;
      }
    },
  };
};

export default ActionExportDataManager;
